export const PRIVACY_SECTIONS = [
  {
    id: 'intro',
    title: 'Introduction',
    body: `cevop ("we", "us", "our") provides a Business Operating System for organisations and their staff — a Business workspace and POS register covering products, stock when needed, buying, sales, money tracking, and reports. This Privacy Policy explains what personal and business data we collect, why we collect it, how we use and share it, and the choices you have.

This policy applies to the cevop website (cevop.com), the Business app (app.cevop.com), the POS app (pos.cevop.com), and related services. By creating an account or using cevop, you agree to this policy.`,
  },
  {
    id: 'controller',
    title: 'Who is responsible for your data',
    body: `cevop is the data controller for information processed through our platform. If you have questions or want to exercise your rights, contact us at privacy@cevop.com.

If you invite staff to your organisation, you are responsible for ensuring you have a lawful basis to share their contact details with us and that they understand how their account will be used.`,
  },
  {
    id: 'collect',
    title: 'Information we collect',
    body: `We collect information in these categories:

**Account & profile** — name, email address, password (stored hashed), role, and organisation membership.

**Business profile** — business name, business type, country, currency, tax settings (including tax registration details you provide), and branch names.

**Operational data you enter** — products and prices, stock levels, sales and payments, expenses, POS open/close counts, customer names you optionally attach to sales, notes, and audit logs of significant actions (voids, refunds, settings changes).

**Technical data** — device/browser type, IP address, session tokens, timestamps, and error logs needed to keep the service secure and reliable.

**Support communications** — messages you send us and our replies.

We do not collect card PANs or process card payments on your behalf. When a cashier records "Card" or "Transfer" on POS, cevop stores the payment method label and amount you entered — not bank or card credentials.`,
  },
  {
    id: 'use',
    title: 'How we use your information',
    body: `We use your data to:

• Provide, maintain, and improve cevop (register, inventory, reports).
• Authenticate users and enforce role-based access within your organisation.
• Compute totals, tax, ledger entries, and exports you request.
• Send transactional email (verification, password reset, invitations).
• Detect abuse, fraud, and security incidents.
• Comply with law and respond to valid legal requests.

We do not sell your personal data. We do not use your sales figures to advertise to your customers.`,
  },
  {
    id: 'legal-basis',
    title: 'Legal basis (where applicable)',
    body: `Depending on your location, we rely on: **contract** (to deliver the service you signed up for), **legitimate interests** (security, product improvement, anonymised analytics), and **consent** where required (for example optional marketing email if you opt in). You may withdraw consent without affecting the lawfulness of processing before withdrawal.`,
  },
  {
    id: 'sharing',
    title: 'When we share information',
    body: `We share data only as needed:

**Within your organisation** — users see data according to their role (cashiers see POS; managers see branch reports; owners see organisation-wide money).

**Service providers** — hosting, email delivery, and error monitoring under contracts that restrict use to providing our service.

**Legal** — if required by law, court order, or to protect rights, safety, and integrity of cevop and users.

**Business transfer** — if cevop is merged or acquired, data may transfer subject to this policy or notice to you.

We do not share your ledger or sales data with other cevop customers.`,
  },
  {
    id: 'retention',
    title: 'Retention',
    body: `We keep account and business records while your organisation is active and for a reasonable period afterward to meet legal, tax, and dispute-resolution obligations. You may request deletion of your organisation subject to limits (for example where we must retain financial records). Backups may persist for a limited time before rotation.`,
  },
  {
    id: 'security',
    title: 'Security',
    body: `We use industry-standard measures: encryption in transit (HTTPS), hashed passwords, tenant isolation at the database layer, role permissions, session expiry, and audit logging for sensitive actions. No online service is perfectly secure; please use a strong password and sign out on shared devices.`,
  },
  {
    id: 'rights',
    title: 'Your rights',
    body: `Depending on applicable law (including Nigeria's NDPR and similar frameworks), you may have the right to access, correct, delete, or export personal data, object to certain processing, or lodge a complaint with a supervisory authority. Organisation owners can close a business from Settings → Business. Admins remove staff from Settings → People. Contact privacy@cevop.com for requests we cannot fulfil in-app.`,
  },
  {
    id: 'cookies',
    title: 'Cookies & local storage',
    body: `cevop uses strictly necessary cookies and browser storage for authentication (session/refresh tokens), theme preference, and branch selection. We do not use third-party advertising cookies on the product apps. The marketing site may use minimal analytics you configure at deploy time; by default we do not load third-party trackers.`,
  },
  {
    id: 'children',
    title: 'Children',
    body: `cevop is a business product not directed at children under 16. We do not knowingly collect data from children.`,
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: `We may update this policy. Material changes will be posted on this page with an updated effective date. Continued use after changes constitutes acceptance where permitted by law.`,
  },
  {
    id: 'contact',
    title: 'Contact',
    body: `Privacy questions and requests: privacy@cevop.com`,
  },
] as const;

export const TERMS_SECTIONS = [
  {
    id: 'agreement',
    title: 'Agreement',
    body: `These Terms of Service ("Terms") govern access to cevop's website and software. By registering or using cevop, you agree to these Terms on behalf of yourself and, if applicable, the business you represent. If you do not agree, do not use the service.`,
  },
  {
    id: 'service',
    title: 'The service',
    body: `cevop provides a Business Operating System including a Business workspace and POS register for products, stock when needed, buying, sales, money, and reports. Features available to you depend on your plan and role. We may add, change, or remove features with reasonable notice where changes materially affect paid plans.`,
  },
  {
    id: 'account',
    title: 'Accounts & eligibility',
    body: `You must provide accurate registration information and keep credentials confidential. You are responsible for all activity under your account and for invitations you send to staff. You must be at least 18 and authorised to bind the business you register. One person may not maintain multiple free-tier accounts to evade limits.`,
  },
  {
    id: 'acceptable-use',
    title: 'Acceptable use',
    body: `You agree not to: violate law; infringe others' rights; upload malware; probe or disrupt our systems; scrape the service without permission; misrepresent sales or tax records; share accounts across unrelated businesses; or use cevop to process transactions for illegal goods or services. We may suspend accounts that violate these rules.`,
  },
  {
    id: 'your-data',
    title: 'Your business data',
    body: `You retain ownership of data you enter. You grant cevop a limited licence to host, process, back up, and display that data solely to provide the service. You are responsible for the accuracy of figures you enter and for complying with tax, licensing, and record-keeping laws in your jurisdiction. cevop is bookkeeping support software, not a substitute for professional accounting or tax advice.`,
  },
  {
    id: 'plans',
    title: 'Plans, billing & limits',
    body: `Plan limits (branches, staff, transactions, modules) are described on our Pricing page and in-product. Paid plans are managed in the Business app when billing is live for your organisation — not on the marketing website. Fees are non-refundable except where required by law. Failure to pay may result in downgrade or suspension after notice. Enterprise is offered by agreement, not self-serve checkout.`,
  },
  {
    id: 'trial',
    title: 'Trials & free tier',
    body: `Starter and trial access may include usage caps. We may modify free-tier limits with notice. Trial features may revert when a trial ends unless you upgrade.`,
  },
  {
    id: 'availability',
    title: 'Availability & support',
    body: `We aim for high availability but do not guarantee uninterrupted access. Maintenance, network failures, and third-party outages may occur. Support channels and response times depend on your plan.`,
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer of warranties',
    body: `cevop is provided "as is" and "as available" to the fullest extent permitted by law. We disclaim warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that calculations (including tax splits) match every local rule without your verification.`,
  },
  {
    id: 'liability',
    title: 'Limitation of liability',
    body: `To the maximum extent permitted by law, cevop and its suppliers are not liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenue, data, or goodwill. Our aggregate liability for claims relating to the service in any twelve-month period is limited to the fees you paid us in that period (or ₦50,000 if you are on a free plan), except where liability cannot be limited by law.`,
  },
  {
    id: 'indemnity',
    title: 'Indemnity',
    body: `You will indemnify cevop against claims arising from your misuse of the service, your business data, or your violation of these Terms or applicable law, except where caused by our gross negligence or wilful misconduct.`,
  },
  {
    id: 'termination',
    title: 'Termination',
    body: `You may stop using cevop at any time. We may suspend or terminate access for breach, non-payment, or risk to the platform. On termination you should export data beforehand; we may delete data after a retention period subject to legal holds.`,
  },
  {
    id: 'law',
    title: 'Governing law',
    body: `These Terms are governed by the laws of the Federal Republic of Nigeria, without regard to conflict-of-law rules. Disputes shall be subject to the exclusive jurisdiction of the courts of Lagos State, Nigeria, unless mandatory consumer protection law in your country requires otherwise.`,
  },
  {
    id: 'changes-terms',
    title: 'Changes',
    body: `We may update these Terms. We will post the revised version with a new effective date. Material changes to paid customers will be communicated by email or in-app notice where practicable.`,
  },
  {
    id: 'contact-terms',
    title: 'Contact',
    body: `Legal and billing questions: legal@cevop.com`,
  },
] as const;

export const LEGAL_EFFECTIVE = '9 August 2026';
